"use client";

import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  InputBase,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  Paper,
  Switch,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import SchoolIcon from "@mui/icons-material/School";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import WifiIcon from "@mui/icons-material/Wifi";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {
  BusinessCenter,
  LocalBar,
  SvgIconComponent,
  TheaterComedy,
} from "@mui/icons-material";
import { RoomData, RoomLayout } from "../../../../types/venue";
import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";

// ─── Default layouts ───────────────────────────────────────────────────────────
export const DEFAULT_LAYOUTS: RoomLayout[] = [
  { id: "theater", name: "Theater", capacity: 0 },
  { id: "banquet", name: "Banquet", capacity: 0 },
  { id: "cocktail", name: "Cocktail", capacity: 0 },
  { id: "classroom", name: "Classroom", capacity: 0 },
  { id: "boardroom", name: "Boardroom", capacity: 0 },
  { id: "u_shape", name: "U-Shape", capacity: 0 },
  { id: "cabaret", name: "Cabaret", capacity: 0 },
  { id: "other", name: "Other", capacity: 0 },
];

const LAYOUT_ICONS: Record<string, SvgIconComponent> = {
  theater: EventSeatIcon,
  banquet: DinnerDiningIcon,
  cocktail: LocalBar,
  classroom: SchoolIcon,
  boardroom: BusinessCenter,
  u_shape: TableRestaurantIcon,
  cabaret: TheaterComedy,
  other: CorporateFareIcon,
};

interface ServiceProvider {
  name: string;
  description: string | null;
}

interface ProviderOption {
  value: string;
  label: string;
}

interface Role {
  id: number;
  name: string;
}

interface RoomsProps {
  rooms: RoomData[];
  onRoomsChange: (rooms: RoomData[]) => void;
}

// ─── Styled components ─────────────────────────────────────────────────────────
const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,
}));

const SectionLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: textcolor,
  marginBottom: "12px",
  marginTop: "24px",
  paddingBottom: "6px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      fontSize: "13px",
    },
  }),
);

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "selectbg",
})<{ selectbg?: string }>(({ selectbg }) => ({
  width: "100%",
  backgroundColor: selectbg,
  borderRadius: "30px",
  fontSize: "13px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": { padding: "10px 14px" },
}));

// ─── Interactive Layout Card ───────────────────────────────────────────────────
export function LayoutCard({
  layout,
  onCapacityChange,
}: {
  layout: RoomLayout;
  onCapacityChange: (value: string) => void;
}) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color400 = reduxTheme.primary_colors?.[400];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const inputRef = useRef<HTMLInputElement>(null);
  const Icon = LAYOUT_ICONS[layout.id] ?? CorporateFareIcon;

  // Card is active if capacity is specified and greater than 0
  const isActive = (layout.capacity ?? 0) > 0;

  const handleCardClick = () => {
    if (!isActive) {
      // Initialize layout with a baseline value to activate it, then auto-focus
      onCapacityChange("1");
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      // Clicking an active card again turns it off completely
      onCapacityChange("");
    }
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        backgroundColor: isActive ? color900 : color800,
        border: `2px solid ${isActive ? color400 : "transparent"}`,
        borderRadius: "14px",
        padding: "16px",
        minWidth: "155px",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        opacity: isActive ? 1 : 0.6,
        "&:hover": {
          opacity: 1,
          border: `2px solid ${isActive ? color400 : color700}`,
        },
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}
      >
        <Box
          sx={{
            width: "28px",
            height: "28px",
            backgroundColor: isActive ? color400 : color900,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background-color 0.2s",
          }}
        >
          <Icon
            sx={{ fontSize: "16px", color: isActive ? color900 : color700 }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: isActive ? color100 : color700,
          }}
        >
          {layout.name}
        </Typography>
      </Box>

      <InputBase
        inputRef={inputRef}
        value={layout.capacity || ""}
        placeholder="Set Capacity"
        onClick={(e) => e.stopPropagation()} // Stop bubbling so input interaction doesn't toggle card state
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "" || /^\d+$/.test(raw)) onCapacityChange(raw);
        }}
        sx={{
          width: "100%",
          backgroundColor: isActive ? color800 : color900,
          borderRadius: "48px",
          padding: "6px 4px",
          fontSize: "11px",
          fontWeight: 500,
          color: isActive ? color100 : color700,
          border: isActive ? `1px solid rgba(255,255,255,0.15)` : "none",
          "& input": { padding: 0, textAlign: "center" },
          "& input::placeholder": { color: color700, opacity: 0.8 },
        }}
      />
    </Box>
  );
}

// ─── Upload Box ────────────────────────────────────────────────────────────────
function UploadBox({
  label,
  onFilesSelected,
  borderColor,
  textcolor,
}: {
  label: string;
  onFilesSelected: (files: File[]) => void;
  borderColor?: string;
  textcolor?: string;
}) {
  const handleClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = label.toLowerCase().includes("floor")
      ? "image/*,application/pdf"
      : "image/*";
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files ?? []);
      onFilesSelected(files);
    };
    input.click();
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        flex: 1,
        minWidth: 200,
        border: `2px dashed ${borderColor}`,
        borderRadius: "16px",
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "opacity 0.2s",
        "& :hover": { opacity: 0.75 },
      }}
    >
      <CloudUploadOutlinedIcon
        sx={{ color: borderColor, fontSize: 32, mb: 1 }}
      />
      <Typography variant="body2" sx={{ color: textcolor, fontSize: "13px" }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Room Form Component ───────────────────────────────────────────────────────
function RoomForm({
  room,
  onUpdate,
  actionButton,
}: {
  room: RoomData;
  onUpdate: (room: RoomData) => void;
  actionButton?: React.ReactNode;
}) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const [serviceProviders, setServiceProviders] = useState<ProviderOption[]>(
    [],
  );
  const [cameraRoles, setCameraRoles] = useState<Role[]>([]);
  const [addProviderOpen, setAddProviderOpen] = useState(false);
  const [newProvider, setNewProvider] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const update = (field: keyof RoomData, value: unknown) =>
    onUpdate({ ...room, [field]: value });

  const changeLayoutCapacity = (layoutId: string, val: string) =>
    update(
      "layouts",
      room.layouts.map((l) =>
        l.id === layoutId
          ? { ...l, capacity: val === "" ? 0 : Number(val) }
          : l,
      ),
    );

  const numericOnly = (val: string) => val === "" || /^\d+$/.test(val);

  const fetchProviders = useCallback(async () => {
    try {
      const response = await apiService.get<ServiceProvider[] | { results?: ServiceProvider[] }>(
        "/api/configurations/internet-service-providers/",
      );
      const providers = Array.isArray(response.data)
        ? response.data
        : response.data.results ?? [];
      setServiceProviders(
        providers.map((p) => ({ value: p.name, label: p.name })),
      );
    } catch (error) {
      console.error("Failed to fetch internet service providers:", error);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    const fetchCameraRoles = async () => {
      try {
        const response = await apiService.get<Role[] | { results?: Role[] }>(
          "/api/crews/roles/?page_size=200",
        );
        const roles = Array.isArray(response.data)
          ? response.data
          : (response.data.results ?? []);
        setCameraRoles(
          roles.filter((role) => role.name.toLowerCase().includes("camera")),
        );
      } catch (error) {
        console.error("Failed to fetch camera roles:", error);
      }
    };

    fetchCameraRoles();
  }, []);

  const handleAddProvider = async () => {
    if (!newProvider.trim()) return;
    try {
      await apiService.post("/api/configurations/internet-service-providers/", {
        name: newProvider,
      });
      await fetchProviders();
      setNewProvider("");
      setAddProviderOpen(false);
      showSuccessToast(
        `New service provider "${newProvider}" added successfully!`,
      );
    } catch (error) {
      const errorData = (error as { response?: { data?: { name?: string[] } } })
        ?.response?.data;
      console.error("Failed to add new provider:", error);
      showErrorToast(errorData?.name?.[0] ?? "An error occurred.");
    }
  };

  const showRouterDistance = room.router_accessibility === "yes";
  const cameraCableEntries = Array.isArray(
    room.distance_of_each_camera_from_livestream_desk,
  )
    ? room.distance_of_each_camera_from_livestream_desk
    : [];
  const distanceValue =
    parseInt(room.router_distance_from_livestream || "5") || 5;

  return (
    <Box>
      {/* ── ROOM BASICS ── */}
      <SectionLabel textcolor={color100}>Room Details</SectionLabel>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <FieldLabel textcolor={color100}>Room Name</FieldLabel>
          <StyledTextField
            placeholder="e.g. Chapel Hall"
            value={room.name}
            onChange={(e) => update("name", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>Capacity</FieldLabel>
          <StyledTextField
            placeholder="e.g. 250"
            value={room.capacity}
            onChange={(e) => {
              if (numericOnly(e.target.value))
                update("capacity", e.target.value);
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <FieldLabel textcolor={color700}>Room Type</FieldLabel>
          <StyledTextField
            placeholder="e.g. Chapel"
            value={room.room_type}
            onChange={(e) => update("room_type", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
      </Box>

      {/* ── LAYOUTS ── */}
      <Box sx={{ mt: 3, mb: 1 }}>
        <SectionLabel textcolor={color100}>
          Select Available Layouts &amp; Capacities
        </SectionLabel>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "visible",
          mx: -3,
          px: 3,
          pb: 1.5,
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.05)",
            borderRadius: "10px",
          },
        }}
      >
        {room.layouts.map((layout) => (
          <LayoutCard
            key={layout.id}
            layout={layout}
            onCapacityChange={(val) => changeLayoutCapacity(layout.id, val)}
          />
        ))}
      </Box>

      {/* ── INTERNET DETAILS ── */}
      <SectionLabel textcolor={color100}>Internet Details</SectionLabel>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FieldLabel textcolor={color100} sx={{ mb: 0 }}>
              Internet Service Provider
            </FieldLabel>
            <IconButton onClick={() => setAddProviderOpen(true)} size="small">
              <AddCircleOutlineOutlined fontSize="small" />
            </IconButton>
          </Box>
          <FormControl fullWidth sx={{ mt: "6px" }}>
            <StyledSelect
              value={room.internet_service_provider || ""}
              displayEmpty
              selectbg={color800}
              onChange={(e) =>
                update("internet_service_provider", String(e.target.value))
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select provider...</span>
                )
              }
            >
              {serviceProviders.map((provider, idx) => (
                <MenuItem key={provider.value + idx} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Wifi Name</FieldLabel>
          <StyledTextField
            placeholder="e.g. VenueWifi"
            value={room.wifi_name || ""}
            onChange={(e) => update("wifi_name", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Password</FieldLabel>
          <StyledTextField
            type={showPassword ? "text" : "password"}
            placeholder="e.g. •••••"
            value={room.wifi_password || ""}
            onChange={(e) => update("wifi_password", e.target.value)}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    sx={{ color: color300, mr: 0.5 }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>Upload Speed</FieldLabel>
          <StyledTextField
            placeholder="e.g. 50"
            value={room.internet_upload_speed || ""}
            onChange={(e) => {
              if (numericOnly(e.target.value))
                update("internet_upload_speed", e.target.value);
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography fontSize="13px" sx={{ color: color700 }}>
                    Mbps
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Paper
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 3,
          backgroundColor: color800,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: color900,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WifiIcon sx={{ color: color100 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                fontSize="14px"
                sx={{ color: color100, opacity: 0.9 }}
              >
                Is router accessible
              </Typography>
              <Typography
                variant="body2"
                fontSize="13px"
                sx={{ color: color700 }}
              >
                Whether the router is physically accessible from the livestream
                desk.
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={room.router_accessibility === "yes"}
            onChange={(e) =>
              update("router_accessibility", e.target.checked ? "yes" : "no")
            }
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: color900 },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: color400,
                opacity: 1,
              },
              "& .MuiSwitch-switchBase.Mui-checked:hover + .MuiSwitch-track": {
                backgroundColor: color400,
              },
            }}
          />
        </Box>
      </Paper>

      {showRouterDistance && (
        <Box sx={{ mt: 4 }}>
          <FieldLabel textcolor={color100}>
            Router Distance from Livedesk
          </FieldLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Slider
                value={Math.min(distanceValue, 100)}
                onChange={(_, value) =>
                  update("router_distance_from_livestream", String(value))
                }
                min={0}
                max={100}
                sx={{
                  color: color800,
                  "& .MuiSlider-thumb": {
                    backgroundColor: color400,
                    width: 15,
                    height: 15,
                  },
                }}
              />
            </Box>
            <TextField
              value={room.router_distance_from_livestream ?? "5"}
              onChange={(e) => {
                if (numericOnly(e.target.value))
                  update("router_distance_from_livestream", e.target.value);
              }}
              size="small"
              inputProps={{
                inputMode: "numeric",
                style: {
                  textAlign: "center",
                  fontSize: "13px",
                  color: color700,
                  padding: "6px 8px",
                  width: "48px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      fontSize="12px"
                      sx={{ color: color300, whiteSpace: "nowrap" }}
                    >
                      m
                    </Typography>
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: color800,
                  borderRadius: "30px",
                  "& fieldset": { border: "none" },
                  "& :hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <SectionLabel textcolor={color100}>
          Camera cable lengths from livestream switch
        </SectionLabel>
        {cameraCableEntries.map(
          (camera, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid size={{ xs: 7, md: 6 }}>
                <FormControl fullWidth>
                  <StyledSelect
                    displayEmpty
                    value={camera.camera_name}
                    selectbg={color800}
                    onChange={(event) => update(
                      "distance_of_each_camera_from_livestream_desk",
                      cameraCableEntries.map(
                        (entry, i) => i === index
                          ? { ...entry, camera_name: String(event.target.value) }
                          : entry,
                      ),
                    )}
                    renderValue={(selected) => String(selected || "Select camera role")}
                  >
                    <MenuItem value="" disabled>Select camera role</MenuItem>
                    {cameraRoles.map((role) => (
                      <MenuItem key={role.id} value={role.name}>{role.name}</MenuItem>
                    ))}
                    {camera.camera_name && !cameraRoles.some((role) => role.name === camera.camera_name) && (
                      <MenuItem value={camera.camera_name}>{camera.camera_name}</MenuItem>
                    )}
                  </StyledSelect>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 4, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cable length (m)"
                  inputProps={{ min: 0, step: "any" }}
                  value={camera.cable_length_m}
                  onChange={(event) => update(
                    "distance_of_each_camera_from_livestream_desk",
                    cameraCableEntries.map(
                      (entry, i) => i === index
                        ? { ...entry, cable_length_m: event.target.value }
                        : entry,
                    ),
                  )}
                />
              </Grid>
              <Grid size={{ xs: 1, md: 2 }}>
                <Button
                  color="error"
                  onClick={() => update(
                    "distance_of_each_camera_from_livestream_desk",
                    cameraCableEntries.filter((_, i) => i !== index),
                  )}
                >
                  Remove
                </Button>
              </Grid>
            </Grid>
          ),
        )}
        <Button
          variant="outlined"
          onClick={() => update(
            "distance_of_each_camera_from_livestream_desk",
            [...cameraCableEntries, { camera_name: "", cable_length_m: "" }],
          )}
        >
          Add camera
        </Button>
      </Box>

      {/* ── PHOTOS & FLOORPLANS ── */}
      <SectionLabel textcolor={color100}>Photos &amp; Floorplans</SectionLabel>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 1,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <UploadBox
          label={`Upload Room Photos${room.photos.length > 0 ? ` (${room.photos.length} selected)` : ""}`}
          onFilesSelected={(files) =>
            update("photos", [...room.photos, ...files])
          }
          borderColor={color300}
          textcolor={color700}
        />
        <UploadBox
          label={`Upload Floorplans${room.floorplans.length > 0 ? ` (${room.floorplans.length} selected)` : ""}`}
          onFilesSelected={(files) =>
            update("floorplans", [...room.floorplans, ...files])
          }
          borderColor={color300}
          textcolor={color700}
        />
      </Box>

      {/* Contextual Action Button Slot Insertion (Bottom right of card context) */}
      {actionButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 4,
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          {actionButton}
        </Box>
      )}

      {/* ── Add Service Provider Dialog ── */}
      <Dialog
        open={addProviderOpen}
        onClose={() => setAddProviderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Service Provider</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FieldLabel textcolor={color100}>Service Provider Name</FieldLabel>
            <StyledTextField
              placeholder="Enter provider name"
              value={newProvider}
              onChange={(e) => setNewProvider(e.target.value)}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProviderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddProvider}
            variant="contained"
            disabled={!newProvider.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Fresh draft initializer ──────────────────────────────────────────────────
const freshDraft = (): RoomData => ({
  id: `draft-${Date.now()}`,
  name: "",
  capacity: "",
  room_type: "",
  layouts: DEFAULT_LAYOUTS.map((l) => ({ ...l })),
  photos: [],
  floorplans: [],
  internet_service_provider: "",
  wifi_name: "",
  wifi_password: "",
  internet_upload_speed: "",
  router_accessibility: "yes",
  router_distance_from_livestream: "5",
  distance_of_each_camera_from_livestream_desk: [],
});

// ─── Main Export Component ─────────────────────────────────────────────────────
export default function Rooms({ rooms, onRoomsChange }: RoomsProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color400 = reduxTheme.primary_colors?.[400];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color300 = reduxTheme.primary_colors?.[300];

  const [expandedRoom, setExpandedRoom] = useState<string | false>(false);
  const [draftRoom, setDraftRoom] = useState<RoomData>(freshDraft);
  const [draftError, setDraftError] = useState("");

  const addRoom = () => {
    if (!draftRoom.name.trim()) {
      setDraftError("Room name is required before adding.");
      return;
    }
    setDraftError("");
    onRoomsChange([...rooms, { ...draftRoom, id: `room-${Date.now()}` }]);
    setDraftRoom(freshDraft());
    showSuccessToast(
      "Room configuration staged and saved to list successfully.",
    );
  };

  const updateRoom = (updated: RoomData) =>
    onRoomsChange(rooms.map((r) => (r.id === updated.id ? updated : r)));

  const deleteRoom = (id: string) => {
    onRoomsChange(rooms.filter((r) => r.id !== id));
    if (expandedRoom === id) setExpandedRoom(false);
  };

  const displayName = (room: RoomData, i: number) =>
    room.name.trim() || `Room ${i + 1}`;

  return (
    <Box sx={{ width: "100%", minHeight: 400 }}>
      {/* 1. Staging / Creator Panel Form block */}
      <Box>
        <Box
          sx={{
            border: `1px solid ${draftError ? "red" : "rgba(255,255,255,0.08)"}`,
            backgroundColor: "rgba(255,255,255,0.01)",
            borderRadius: "16px",
            mb: 1,
          }}
        >
          <RoomForm
            room={draftRoom}
            onUpdate={(r) => {
              setDraftError("");
              setDraftRoom(r);
            }}
            actionButton={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addRoom}
                sx={{
                  backgroundColor: color400,
                  color: color800,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  "&:hover": { backgroundColor: color100 },
                }}
              >
                Add Room
              </Button>
            }
          />
        </Box>
        {draftError && (
          <Typography sx={{ color: "red", fontSize: "12px", mb: 2, pl: 1 }}>
            {draftError}
          </Typography>
        )}
      </Box>

      {/* 2. List of Saved/Staged Rooms */}
      {rooms.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: color100,
              mb: 2,
              pl: 0.5,
            }}
          >
            Rooms Added ({rooms.length})
          </Typography>

          {rooms.map((room, i) => (
            <Accordion
              key={room.id}
              expanded={expandedRoom === room.id}
              onChange={(_, isExpanded) =>
                setExpandedRoom(isExpanded ? room.id : false)
              }
              elevation={0}
              sx={{
                border: "1px solid rgba(255,255,255,0.08)",
                backgroundColor: "transparent",
                borderRadius: "12px !important",
                mb: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: color700 }} />}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.02)",
                  borderBottom:
                    expandedRoom === room.id
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "none",
                  "& .MuiAccordionSummary-content": { my: 1.5 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    pr: 1,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 500, fontSize: "13px", color: color100 }}
                  >
                    {displayName(room, i)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRoom(room.id);
                    }}
                    sx={{
                      color: "red",
                      opacity: 0.7,
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, backgroundColor: "transparent" }}>
                <RoomForm room={room} onUpdate={updateRoom} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {rooms.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            mt: 6,
            p: 4,
            border: "1px dashed rgba(255,255,255,0.05)",
            borderRadius: "12px",
          }}
        >
          <Typography sx={{ color: color300, fontSize: "13px" }}>
            No saved rooms added yet. Complete the configuration panel above and
            click Save Room to List.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
